import { ArrayBufferTarget, Muxer } from 'mp4-muxer';

// High 4.0, Main 4.0, Baseline 4.0, then High 5.1 for pieces above 1080p.
const CODECS = ['avc1.640028', 'avc1.4d0028', 'avc1.42e028', 'avc1.640033'];
const KEYFRAME_INTERVAL_S = 2;
const QUEUE_LIMIT = 4;
const AUDIO_CODEC = 'mp4a.40.2';
const AUDIO_BITRATE = 192_000;
const AUDIO_CHUNK_FRAMES = 4096;

export type Mp4EncoderConfig = {
  width: number;
  height: number;
  fps: number;
  bitrate: number;
  audio?: AudioBuffer | null;
};

export type Mp4Encoder = {
  addFrame: (source: CanvasImageSource, index: number) => Promise<void>;
  finish: () => Promise<Blob>;
  abort: () => void;
};

export const isMp4EncodingSupported = (): boolean =>
  typeof VideoEncoder !== 'undefined' &&
  typeof VideoFrame !== 'undefined' &&
  typeof AudioEncoder !== 'undefined' &&
  typeof AudioData !== 'undefined';

const pickCodec = async (config: Omit<VideoEncoderConfig, 'codec'>): Promise<string | null> => {
  for (const codec of CODECS) {
    const { supported } = await VideoEncoder.isConfigSupported({ ...config, codec }).catch(() => ({
      supported: false
    }));
    if (supported) return codec;
  }
  return null;
};

const encodeAudioTrack = async (buffer: AudioBuffer, muxer: Muxer<ArrayBufferTarget>): Promise<void> => {
  const { sampleRate, numberOfChannels, length } = buffer;
  const channels = Array.from({ length: numberOfChannels }, (_, channel) => buffer.getChannelData(channel));
  const planar = new Float32Array(AUDIO_CHUNK_FRAMES * numberOfChannels);

  let failure: Error | null = null;
  const encoder = new AudioEncoder({
    output: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
    error: error => {
      failure = error;
    }
  });
  encoder.configure({ codec: AUDIO_CODEC, sampleRate, numberOfChannels, bitrate: AUDIO_BITRATE });

  for (let offset = 0; offset < length; offset += AUDIO_CHUNK_FRAMES) {
    const frames = Math.min(AUDIO_CHUNK_FRAMES, length - offset);
    channels.forEach((data, channel) => planar.set(data.subarray(offset, offset + frames), channel * frames));

    const audioData = new AudioData({
      format: 'f32-planar',
      sampleRate,
      numberOfChannels,
      numberOfFrames: frames,
      timestamp: Math.round((offset / sampleRate) * 1e6),
      data: planar.subarray(0, frames * numberOfChannels)
    });

    try {
      encoder.encode(audioData);
    } finally {
      audioData.close();
    }
  }

  await encoder.flush();
  encoder.close();
  if (failure) throw failure;
};

export const createMp4Encoder = async ({
  width,
  height,
  fps,
  bitrate,
  audio
}: Mp4EncoderConfig): Promise<Mp4Encoder> => {
  const base = { width, height, framerate: fps, bitrate };
  const codec = await pickCodec(base);
  if (!codec) throw new Error(`No H.264 encoder available for ${width}x${height}`);

  const target = new ArrayBufferTarget();
  const muxer = new Muxer({
    target,
    video: { codec: 'avc', width, height, frameRate: fps },
    ...(audio
      ? { audio: { codec: 'aac' as const, numberOfChannels: audio.numberOfChannels, sampleRate: audio.sampleRate } }
      : {}),
    fastStart: 'in-memory'
  });

  // Cheap next to the video, and doing it up front fails early if the mix is broken.
  if (audio) await encodeAudioTrack(audio, muxer);

  let failure: Error | null = null;

  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: error => {
      failure = error;
    }
  });

  encoder.configure({ ...base, codec, hardwareAcceleration: 'prefer-hardware', latencyMode: 'quality' });

  const keyFrameEvery = Math.max(1, Math.round(fps * KEYFRAME_INTERVAL_S));
  const frameDuration = Math.round(1e6 / fps);

  // Waiting on `dequeue` rather than a timer keeps throughput up in background tabs.
  const drain = async (): Promise<void> => {
    while (encoder.encodeQueueSize > QUEUE_LIMIT) {
      await new Promise<void>(resolve => encoder.addEventListener('dequeue', () => resolve(), { once: true }));
    }
  };

  const addFrame = async (source: CanvasImageSource, index: number): Promise<void> => {
    if (failure) throw failure;
    await drain();

    // Derive each timestamp from its index so rounding never accumulates.
    const timestamp = Math.round((index * 1e6) / fps);
    const frame = new VideoFrame(source, { timestamp, duration: frameDuration });
    try {
      encoder.encode(frame, { keyFrame: index % keyFrameEvery === 0 });
    } finally {
      frame.close();
    }
  };

  const finish = async (): Promise<Blob> => {
    await encoder.flush();
    if (failure) throw failure;
    encoder.close();
    muxer.finalize();
    return new Blob([target.buffer], { type: 'video/mp4' });
  };

  const abort = (): void => {
    if (encoder.state !== 'closed') encoder.close();
  };

  return { addFrame, finish, abort };
};
